import cn from "@/v2-ui/utils/utils.cn"
import type { DiffSegment } from "@/v2-backoffice-client/components/diff/diff.utils"

type DiffSide = "old" | "new" | "same"

export type DiffLineProps = {
  // "old" renders the equal + deleted segments on a red line, "new" the
  // equal + added segments on a green one, "same" the whole stream untinted
  // for a value that did not move.
  side: DiffSide
  // Short tag naming where this side's value comes from ("code", "DB",
  // "before", …). Fixed-width, so the values line up in a column.
  origin: string
  // Tooltip on the origin tag, for sides whose provenance isn't obvious.
  originTitle?: string
  segments: DiffSegment[]
  // Italic placeholder for a side that holds no text at all.
  empty: string
}

const SIGN: Record<DiffSide, string> = { old: "−", new: "+", same: " " }

/**
 * One line of a git-style diff: sign gutter, origin tag, then the text with
 * the changed words emphasised.
 *
 * Shared by every before/after surface in the app (text sync, pending
 * publish, layer configuration drift) so a red line always means the same
 * thing wherever the operator meets one.
 */
function DiffLine(props: DiffLineProps) {
  const { side, origin, originTitle, segments, empty } = props

  const visible = segments.filter((segment) =>
    side === "old"
      ? segment.op !== "add"
      : side === "new"
        ? segment.op !== "del"
        : true,
  )
  const hasText = visible.some((segment) => segment.text.trim() !== "")

  return (
    <div
      className={cn(
        "diff-line",
        side === "old" && "diff-line-del",
        side === "new" && "diff-line-add",
      )}
    >
      <span className="diff-line-sign">{SIGN[side]}</span>
      <span
        className={cn("diff-line-origin", originTitle && "cursor-help")}
        title={originTitle}
      >
        {origin}
      </span>
      <span
        className={cn(
          "min-w-0 whitespace-pre-wrap break-words",
          side === "same" && "text-muted-foreground",
        )}
      >
        {hasText ? (
          visible.map((segment, i) => (
            <span
              key={i}
              className={
                segment.op === "equal"
                  ? undefined
                  : side === "old"
                    ? "diff-word-del"
                    : "diff-word-add"
              }
            >
              {segment.text}
            </span>
          ))
        ) : (
          <span className="italic text-muted-foreground">{empty}</span>
        )}
      </span>
    </div>
  )
}

export default DiffLine
